import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import { useCanCheck } from '@/ability/helpers';
import {
    getProjectRequests,
    respondToProjectRequest as respondToProjectRequestApi,
    createProjectFromProjectRequest as createProjectFromProjectRequestApi,
    createAuditFromProjectRequest as createAuditFromProjectRequestApi,
} from '@services/open-market.service';
import { PROJECTS_QUERY_KEY } from '@hooks/projects/useProjects';
import { PROJECT_CLIENT_TYPE } from '@/constants/projectClientType';

export const PROJECT_REQUESTS_QUERY_KEY = ['project-requests'];

export const formatProjectRequestDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export const formatProjectRequestAmount = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return `$${num.toLocaleString()}`;
};

function getIsClientProfile(user, role) {
    return (
        user?.isClient === true ||
        (typeof role === 'string' && role.toLowerCase() === 'client') ||
        (typeof user?.role === 'string' && user?.role.toLowerCase() === 'client') ||
        (Array.isArray(user?.roles) && user.roles.some((r) => String(r).toLowerCase() === 'client'))
    );
}

/** Build POST /api/v1/project-requests/:id/create-project payload from a request record. */
function buildCreateProjectPayload(record) {
    const name = record.openMarketLead?.title?.trim() || 'Project';
    const clientId = record.requester?.id ?? null;
    const amount = record.bid?.amount;
    const budget = amount != null ? Number(amount) : 0;
    const priority = 'high';
    return { name, clientId, budget, priority, clientType: PROJECT_CLIENT_TYPE.MINE_AND_PLATFORM };
}

function buildCreateAuditPayload(record) {
    const leadTitle = record.openMarketLead?.title?.trim();
    return {
        name: leadTitle ? `${leadTitle} Audit` : 'Audit',
        description: '',
        clientId: record.requester?.id ?? null,
    };
}

/**
 * Fetch paginated project requests (GET /api/v1/project-requests).
 * @param {{ page?: number, limit?: number, enabled?: boolean }} options
 * @returns {{ items, total, totalPages, page, setPage, limit, isLoading, error, isClient, canRespond, respondToRequest, respondPending, createProjectFromRequest, createProjectPending }}
 */
export function useProjectRequests(options = {}) {
    const { limit: initialLimit = 10, enabled = true } = options;
    const [page, setPage] = useState(1);
    const limit = initialLimit;
    const queryClient = useQueryClient();
    const user = useAuthStore((s) => s.user);
    const role = useAuthStore((s) => s.role);
    const can = useCanCheck();

    const isClient = useMemo(() => getIsClientProfile(user, role), [user, role]);
    const canRespond = can('project_requests.requests.respond');

    const { data, isLoading, error } = useQuery({
        queryKey: [...PROJECT_REQUESTS_QUERY_KEY, { page, limit }],
        queryFn: () => getProjectRequests({ page, limit }),
        enabled,
    });

    const respondMutation = useMutation({
        mutationFn: ({ requestId, action, note }) => respondToProjectRequestApi(requestId, { action, note }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PROJECT_REQUESTS_QUERY_KEY });
        },
    });

    const createProjectMutation = useMutation({
        mutationFn: ({ requestId, body }) => createProjectFromProjectRequestApi(requestId, body),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
            await queryClient.refetchQueries({ queryKey: PROJECTS_QUERY_KEY });
        },
    });

    const createAuditMutation = useMutation({
        mutationFn: ({ requestId, body }) => createAuditFromProjectRequestApi(requestId, body),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: PROJECT_REQUESTS_QUERY_KEY });
            await queryClient.refetchQueries({ queryKey: PROJECT_REQUESTS_QUERY_KEY });
        },
    });

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = data?.totalPages ?? 1;

    const setPageSafe = useCallback((nextPage) => {
        setPage((p) => (typeof nextPage === 'function' ? nextPage(p) : nextPage));
    }, []);

    const respondToRequest = useCallback(
        (requestId, { action, note }, callbacks = {}) => {
            respondMutation.mutate(
                { requestId, action, note: note ?? '' },
                {
                    onSuccess: callbacks.onSuccess,
                    onError: callbacks.onError,
                },
            );
        },
        [respondMutation],
    );

    const createProjectFromRequest = useCallback(
        (record, callbacks = {}) => {
            const payload = buildCreateProjectPayload(record);
            if (!payload.clientId) {
                callbacks.onError?.(new Error('Requester is missing'));
                return;
            }
            createProjectMutation.mutate(
                { requestId: record.id, body: payload },
                {
                    onSuccess: callbacks.onSuccess,
                    onError: callbacks.onError,
                },
            );
        },
        [createProjectMutation],
    );

    /** Create project with explicit payload (e.g. from modal form). */
    const createProjectWithPayload = useCallback(
        (payload, callbacks = {}) => {
            if (!payload?.requestId) {
                callbacks.onError?.(new Error('Project request is required'));
                return;
            }
            if (!payload?.body?.clientId) {
                callbacks.onError?.(new Error('Client is required'));
                return;
            }
            createProjectMutation.mutate(
                { requestId: payload.requestId, body: payload.body },
                {
                    onSuccess: callbacks.onSuccess,
                    onError: callbacks.onError,
                },
            );
        },
        [createProjectMutation],
    );

    const createAuditFromRequest = useCallback(
        (record, callbacks = {}) => {
            const payload = buildCreateAuditPayload(record);
            if (!payload.clientId) {
                callbacks.onError?.(new Error('Requester is missing'));
                return;
            }
            createAuditMutation.mutate(
                { requestId: record.id, body: payload },
                {
                    onSuccess: callbacks.onSuccess,
                    onError: callbacks.onError,
                },
            );
        },
        [createAuditMutation],
    );

    const createAuditWithPayload = useCallback(
        (payload, callbacks = {}) => {
            if (!payload?.requestId) {
                callbacks.onError?.(new Error('Project request is required'));
                return;
            }
            if (!payload?.body?.clientId) {
                callbacks.onError?.(new Error('Client is required'));
                return;
            }
            createAuditMutation.mutate(
                { requestId: payload.requestId, body: payload.body },
                {
                    onSuccess: callbacks.onSuccess,
                    onError: callbacks.onError,
                },
            );
        },
        [createAuditMutation],
    );

    return {
        items,
        total,
        totalPages,
        page,
        setPage: setPageSafe,
        limit,
        isLoading,
        error: error ?? null,
        isClient,
        canRespond,
        respondToRequest,
        respondPending: respondMutation.isPending,
        createProjectFromRequest,
        createProjectWithPayload,
        createProjectPending: createProjectMutation.isPending,
        createAuditFromRequest,
        createAuditWithPayload,
        createAuditPending: createAuditMutation.isPending,
    };
}
